/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useMemo, useState } from 'react';
import { GetHumioDashboardsQuery } from '../../generated/graphql';
import { HumioIntegrationContext } from '../HumioIntegrationProvider';

/**
 * TODO: MIGRATION REQUIRED - LogScaleCompat is a temporary compatibility layer
 *
 * This file provides backward compatibility for components still using the old LogScaleContext.
 * It should be removed once all components are migrated to use HumioIntegrationContext directly.
 *
 * MIGRATION CHECKLIST:
 * ----------------
 * Components still using LogScaleContext that need migration:
 *
 * TODO: [ ] HumioSearch.tsx
 *   - Replace: useContext(LogScaleContext)
 *   - With: useContext(HumioIntegrationContext) or useLogScale() hook
 *
 * TODO: [ ] HumioDashboards.tsx
 *   - Replace: useContext(LogScaleContext)
 *   - With: useContext(HumioIntegrationContext) or useLogScale() hook
 *
 * TODO: [ ] HumioDashboardOverview components
 *   - Update all Humio dashboard components to use new context
 *
 * TODO: [ ] useReloadHumioUI.ts
 *   - Replace: useContext(LogScaleContext)
 *   - With: useContext(HumioIntegrationContext) or useLogScale() hook
 *
 * TODO: [ ] Any other components importing LogScaleContext
 *
 * MIGRATION STEPS:
 * ---------------
 * 1. For each component:
 *    a. Replace `import { LogScaleContext } from '../shared/providers'`
 *       with `import { useLogScale } from '../shared/providers'`
 *    b. Replace `const { ... } = useContext(LogScaleContext)`
 *       with `const { ... } = useLogScale()`
 *
 * 2. Update property mappings:
 *    - sessionIdTokenCorelight → token (from useHumioToken() hook)
 *    - logScaleDashboardsLoading → dashboardsLoading
 *    - logScaleDashboardsData → dashboardsData
 *    - logScaleDashboardsError → dashboardsError
 *
 * 3. Handle local state (logScaleDashboardParams):
 *    - Move to component local state or create a separate context if shared
 *
 * 4. Remove LogScaleProvider wrapper from App.tsx once migration complete
 *
 * 5. Delete this file and remove exports from index.ts
 *
 * DEPRECATION WARNING:
 * ------------------
 * @deprecated LogScaleContext is deprecated. Use HumioIntegrationContext or the
 * useLogScale() hook instead. This compatibility layer will be removed in a future version.
 */

// Create a context that matches the old LogScaleContext structure
export interface LogScaleCompatContext {
  logScaleURL?: string;
  logScaleDashboardId?: string;
  logScaleURLParams?: string[] | [];
  logScaleDashboardsLoading?: boolean;
  logScaleDashboardsData?: GetHumioDashboardsQuery;
  logScaleDashboardsError?: unknown;
  setLogScaleURL?: (url: string | undefined) => void;
  setLogScaleDashboardId?: (id: string | undefined) => void;
  setLogScaleURLParams?: (params: string[] | [] | undefined) => void;
  setLogScaleDashboardsLoading?: (loading: boolean) => void;
  setLogScaleDashboardsData?: (data: unknown) => void;
  setLogScaleDashboardsError?: (error: unknown) => void;
  iframeKey?: number;
  setIframeKey?: (key: number | ((prev: number) => number)) => void;
  logScaleDashboardParams?: Record<string, unknown>;
  setLogScaleDashboardParams?: (params: Record<string, unknown>) => void;
  sessionIdTokenCorelight?: string;
  setSessionIdTokenCorelight?: (token: string | undefined) => void;
  forceRefreshDashboards?: () => Promise<void>;
}

/**
 * @deprecated Use HumioIntegrationContext instead
 */
export const LogScaleContext = React.createContext<LogScaleCompatContext>({});

/**
 * Compatibility provider that wraps HumioIntegrationContext
 * @deprecated This is a temporary compatibility layer. Components should migrate to HumioIntegrationContext
 */
export const LogScaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const humioContext = useContext(HumioIntegrationContext);

  // TODO: This local state should be moved to components or a separate context
  // Local state for dashboard params (not in new context)
  const [logScaleDashboardParams, setLogScaleDashboardParams] = useState<
    Record<string, unknown> | undefined
  >();

  // Create a compatibility layer
  const compatValue = useMemo(() => {
    if (!humioContext) {
      // TODO: Add warning in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'LogScaleProvider: HumioIntegrationContext not found. Make sure HumioIntegrationProvider is in the component tree.',
        );
      }
      return {};
    }

    return {
      // Map state properties to flat structure
      logScaleURL: humioContext.state?.logScaleURL,
      logScaleDashboardId: humioContext.state?.logScaleDashboardId,
      logScaleURLParams: humioContext.state?.logScaleURLParams,
      logScaleDashboardsLoading: humioContext.state?.dashboardsLoading,
      logScaleDashboardsData: humioContext.state?.dashboardsData,
      logScaleDashboardsError: humioContext.state?.dashboardsError,
      iframeKey: humioContext.state?.iframeKey,

      // This mapping is critical - old components expect sessionIdTokenCorelight
      sessionIdTokenCorelight: humioContext.state?.token,

      // Map methods
      setLogScaleURL: humioContext.setLogScaleURL,
      setLogScaleDashboardId: humioContext.setLogScaleDashboardId,
      setLogScaleURLParams: humioContext.setLogScaleURLParams,
      forceRefreshDashboards: humioContext.forceRefreshDashboards,

      // Handle setIframeKey with callback support
      setIframeKey: (keyOrCallback: number | ((prev: number) => number)) => {
        if (typeof keyOrCallback === 'function') {
          const currentKey = humioContext.state?.iframeKey || 0;
          const newKey = keyOrCallback(currentKey);
          humioContext.setIframeKey(newKey);
        } else {
          humioContext.setIframeKey(keyOrCallback);
        }
      },

      // TODO: These no-ops indicate missing functionality in the new context
      // Verify if these are actually needed before removing LogScaleCompat
      setLogScaleDashboardsLoading: () => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'setLogScaleDashboardsLoading is deprecated and has no effect',
          );
        }
      },
      setLogScaleDashboardsData: () => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'setLogScaleDashboardsData is deprecated and has no effect',
          );
        }
      },
      setLogScaleDashboardsError: () => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'setLogScaleDashboardsError is deprecated and has no effect',
          );
        }
      },

      // TODO: Move this local state to appropriate location during migration
      // Local state for dashboard params
      logScaleDashboardParams,
      setLogScaleDashboardParams,
    };
  }, [humioContext, logScaleDashboardParams]);

  return (
    <LogScaleContext.Provider value={compatValue}>
      {children}
    </LogScaleContext.Provider>
  );
};
